import React, {
  useState,
  useEffect,
} from "react"

import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import PulseLoader from "react-spinners/PulseLoader"

import EtBtnFormBasic
  from "@/components/common/buttons/EtBtnFormBasic"

import Modal
  from "@/components/common/modal/Modal"

import {
  useGetOrgIntentionQuestionsQuery,
  useCreateNewOrgIntentionQuestionMutation,
  useUpdateOrgIntentionQuestionMutation,
  useDeleteOrgIntentionQuestionMutation,
} from "@/features/orgIntentionBoards/orgIntentionQuestionsApiSlice"

const EtOrgIntentionQuestionManager = ({
  intentionBoardId,
}) => {

  // First Created - 04 Sep 2026
  // Author - Atul Pandey
  // Purpose - Manage questions for a Trinity Intention Board.

  /*
  |--------------------------------------------------------------------------
  | 1 - State
  |--------------------------------------------------------------------------
  */

  const [
    showManager,
    setShowManager,
  ] =
    useState(true)

  const [
    editQuestionId,
    setEditQuestionId,
  ] =
    useState(null)

  const [
    isDeleteModalOpen,
    setIsDeleteModalOpen,
  ] =
    useState(false)

  const [
    deleteQuestionId,
    setDeleteQuestionId,
  ] =
    useState(null)

  /*
  |--------------------------------------------------------------------------
  | 2 - Get Questions
  |--------------------------------------------------------------------------
  */

  const {
    data:
      orgIntentionQuestionsData,

    isLoading:
      isLoadingQuestions,

    isError:
      isQuestionsError,

    error:
      questionsError,

  } =
    useGetOrgIntentionQuestionsQuery({
      intentionBoardId,
    })

  /*
  |--------------------------------------------------------------------------
  | 3 - Mutations
  |--------------------------------------------------------------------------
  */

  const [
    createNewOrgIntentionQuestion,
    {
      isLoading:
        isCreating,

      isSuccess:
        isCreateSuccess,

      isError:
        isCreateError,

      error:
        createError,
    },
  ] =
    useCreateNewOrgIntentionQuestionMutation()

  const [
    updateOrgIntentionQuestion,
    {
      isLoading:
        isUpdating,

      isSuccess:
        isUpdateSuccess,

      isError:
        isUpdateError,

      error:
        updateError,
    },
  ] =
    useUpdateOrgIntentionQuestionMutation()

  const [
    deleteOrgIntentionQuestion,
    {
      isLoading:
        isDeleting,

      isError:
        isDeleteError,

      error:
        deleteError,
    },
  ] =
    useDeleteOrgIntentionQuestionMutation()

  /*
  |--------------------------------------------------------------------------
  | 4 - Normalize Questions
  |--------------------------------------------------------------------------
  */

  const orgIntentionQuestions =
    orgIntentionQuestionsData
      ?.ids
      ?.map(
        id =>
          orgIntentionQuestionsData
            ?.entities[id]
      )
      ?.sort(
        (
          a,
          b
        ) =>
          (
            a?.displaySequence ||
            a?.questionNumber ||
            0
          ) -
          (
            b?.displaySequence ||
            b?.questionNumber ||
            0
          )
      ) ||
    []

  /*
  |--------------------------------------------------------------------------
  | 5 - Initial Form State
  |--------------------------------------------------------------------------
  */

  const getNextQuestionNumber =
    () => {

      if (
        !orgIntentionQuestions
          ?.length
      ) {

        return 1

      }

      const highestQuestionNumber =
        Math.max(
          ...orgIntentionQuestions.map(
            question =>
              question
                ?.questionNumber ||
              0
          )
        )

      return (
        highestQuestionNumber +
        1
      )

    }

  const getInitialFormState =
    () => ({

      questionNumber:
        getNextQuestionNumber(),

      questionText:
        "",

      questionDescription:
        "",

      responseGuidance:
        "",

      questionType:
        "reflection",

      questionPurpose:
        "reflection",

      isRequired:
        true,

      displaySequence:
        getNextQuestionNumber(),

      isActive:
        true,

      minimumResponseLength:
        0,

      maximumResponseLength:
        "",

      options:
        [],

    })

  const [
    formData,
    setFormData,
  ] =
    useState(
      getInitialFormState()
    )

  /*
  |--------------------------------------------------------------------------
  | 6 - Reset After Create / Update
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (
      isCreateSuccess ||
      isUpdateSuccess
    ) {

      setEditQuestionId(
        null
      )

      setFormData(
        getInitialFormState()
      )

    }

  }, [
    isCreateSuccess,
    isUpdateSuccess,
    orgIntentionQuestionsData,
  ])

  /*
  |--------------------------------------------------------------------------
  | 7 - Handle Change
  |--------------------------------------------------------------------------
  */

  const handleChange =
    e => {

      const {
        name,
        value,
        type,
        checked,
      } =
        e.target

      setFormData(
        prev => ({

          ...prev,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 8 - Question Type Change
  |--------------------------------------------------------------------------
  */

  const handleQuestionTypeChange =
    e => {

      const value =
        e.target.value

      setFormData(
        prev => ({

          ...prev,

          questionType:
            value,

          options:
            (
              value ===
                "single_select" ||
              value ===
                "multi_select"
            )
              ? (
                  prev.options
                    ?.length
                    ? prev.options
                    : [
                        {
                          optionLabel:
                            "",
                          optionValue:
                            "",
                          displaySequence:
                            1,
                        },
                      ]
                )
              : [],

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 9 - Add Option
  |--------------------------------------------------------------------------
  */

  const handleAddOption =
    () => {

      setFormData(
        prev => ({

          ...prev,

          options: [
            ...prev.options,

            {
              optionLabel:
                "",

              optionValue:
                "",

              displaySequence:
                prev.options.length +
                1,
            },
          ],

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 10 - Update Option
  |--------------------------------------------------------------------------
  */

  const handleOptionChange =
    (
      index,
      key,
      value
    ) => {

      const updatedOptions =
        formData.options.map(
          (
            option,
            optionIndex
          ) => {

            if (
              optionIndex !==
              index
            ) {
              return option
            }

            return {
              ...option,

              [key]:
                value,
            }

          }
        )

      setFormData(
        prev => ({

          ...prev,

          options:
            updatedOptions,

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 11 - Remove Option
  |--------------------------------------------------------------------------
  */

  const handleRemoveOption =
    index => {

      const updatedOptions =
        formData.options
          .filter(
            (
              option,
              optionIndex
            ) =>
              optionIndex !==
              index
          )
          .map(
            (
              option,
              optionIndex
            ) => ({

              ...option,

              displaySequence:
                optionIndex +
                1,

            })
          )

      setFormData(
        prev => ({

          ...prev,

          options:
            updatedOptions,

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 12 - Edit Question
  |--------------------------------------------------------------------------
  */

  const handleEditQuestion =
    question => {

      setEditQuestionId(
        question._id
      )

      setFormData({

        questionNumber:
          question
            ?.questionNumber ||
          "",

        questionText:
          question
            ?.questionText ||
          "",

        questionDescription:
          question
            ?.questionDescription ||
          "",

        responseGuidance:
          question
            ?.responseGuidance ||
          "",

        questionType:
          question
            ?.questionType ||
          "reflection",

        questionPurpose:
          question
            ?.questionPurpose ||
          "reflection",

        isRequired:
          question
            ?.isRequired ??
          true,

        displaySequence:
          question
            ?.displaySequence ||
          question
            ?.questionNumber ||
          "",

        isActive:
          question
            ?.isActive ??
          true,

        minimumResponseLength:
          question
            ?.minimumResponseLength ??
          0,

        maximumResponseLength:
          question
            ?.maximumResponseLength ??
          "",

        options:
          question
            ?.options
            ?.map(
              option => ({

                optionLabel:
                  option
                    ?.optionLabel ||
                  "",

                optionValue:
                  option
                    ?.optionValue ||
                  "",

                displaySequence:
                  option
                    ?.displaySequence ||
                  0,

              })
            ) ||
          [],

      })

    }

  /*
  |--------------------------------------------------------------------------
  | 13 - Cancel Edit
  |--------------------------------------------------------------------------
  */

  const handleCancelEdit =
    () => {

      setEditQuestionId(
        null
      )

      setFormData(
        getInitialFormState()
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 14 - Submit
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async e => {

      e.preventDefault()

      const questionData = {

        intentionBoardId,

        questionNumber:
          Number(
            formData.questionNumber
          ),

        questionText:
          formData.questionText,

        questionDescription:
          formData.questionDescription,

        responseGuidance:
          formData.responseGuidance,

        questionType:
          formData.questionType,

        questionPurpose:
          formData.questionPurpose,

        isRequired:
          formData.isRequired,

        displaySequence:
          Number(
            formData.displaySequence
          ),

        isActive:
          formData.isActive,

        minimumResponseLength:
          Number(
            formData.minimumResponseLength ||
            0
          ),

        maximumResponseLength:
          formData.maximumResponseLength
            ? Number(
                formData.maximumResponseLength
              )
            : null,

        options:
          (
            formData.questionType ===
              "single_select" ||
            formData.questionType ===
              "multi_select"
          )
            ? formData.options
                .filter(
                  option =>
                    option
                      ?.optionLabel
                      ?.trim()
                )
                .map(
                  (
                    option,
                    index
                  ) => ({

                    optionLabel:
                      option.optionLabel,

                    optionValue:
                      option.optionValue ||
                      option.optionLabel,

                    displaySequence:
                      index +
                      1,

                  })
                )
            : [],

      }

      try {

        if (
          editQuestionId
        ) {

          await updateOrgIntentionQuestion({

            intentionQuestionId:
              editQuestionId,

            ...questionData,

          })
            .unwrap()

        } else {

          await createNewOrgIntentionQuestion(
            questionData
          )
            .unwrap()

        }

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 15 - Delete
  |--------------------------------------------------------------------------
  */

  const handleOpenDeleteModal =
    intentionQuestionId => {

      setDeleteQuestionId(
        intentionQuestionId
      )

      setIsDeleteModalOpen(
        true
      )

    }

  const handleCloseDeleteModal =
    () => {

      setDeleteQuestionId(
        null
      )

      setIsDeleteModalOpen(
        false
      )

    }

  const handleDeleteQuestion =
    async e => {

      e.preventDefault()

      if (
        !deleteQuestionId
      ) {
        return
      }

      try {

        await deleteOrgIntentionQuestion({

          intentionQuestionId:
            deleteQuestionId,

          intentionBoardId,

        })
          .unwrap()

      } catch (
        error
      ) {

        console.error(
          error
        )

      } finally {

        handleCloseDeleteModal()

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 16 - Loading / Errors
  |--------------------------------------------------------------------------
  */

  const isLoading =
    isLoadingQuestions ||
    isCreating ||
    isUpdating ||
    isDeleting

  const mutationError =
    createError ||
    updateError ||
    deleteError

  const mutationIsError =
    isCreateError ||
    isUpdateError ||
    isDeleteError

  /*
  |--------------------------------------------------------------------------
  | 17 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="m-1 p-1 bg-white rounded">

      {/* Header */}

      <div className="flex justify-between items-center m-1 p-1">

        <div className="flex justify-start items-center">

          <p className="font-bold text-lg">
            Intention Questions
          </p>

          <div
            onClick={
              () =>
                setShowManager(
                  prev =>
                    !prev
                )
            }
            className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-gray-100"
          >

            {showManager ? (

              <MinusIcon className="h-5 w-5 text-orange-600" />

            ) : (

              <PlusIcon className="h-4 w-4 text-orange-600" />

            )}

          </div>

        </div>

      </div>

      {showManager && (

        <div>

          {/* Loading */}

          {isLoading && (

            <PulseLoader
              color={"#ea580c"}
              size={6}
            />

          )}

          {/* Query Error */}

          {isQuestionsError && (

            <p className="text-orange-700 text-xs p-2">
              {questionsError
                ?.data
                ?.message ||
                "Unable to load Intention Board questions"}
            </p>

          )}

          {/* Mutation Error */}

          {mutationIsError && (

            <p className="text-orange-700 text-xs p-2">
              {mutationError
                ?.data
                ?.message ||
                "Unable to save Intention Board question"}
            </p>

          )}

          {/* Existing Questions */}

          <div className="bg-gray-200 rounded m-1 p-1">

            <p className="font-bold text-xs m-2">
              Existing Questions
            </p>

            {orgIntentionQuestions
              ?.length === 0 && (

              <div className="bg-white rounded m-1 p-2">

                <p className="text-xs text-gray-500">
                  No Intention Board questions have been added yet.
                </p>

              </div>

            )}

            {orgIntentionQuestions
              ?.map(
                (
                  question,
                  index
                ) => (

                  <div
                    key={
                      question._id
                    }
                    className="bg-white rounded m-1 p-2"
                  >

                    <div className="flex justify-between items-start">

                      <div className="flex-1">

                        <div className="flex justify-start items-center flex-wrap">

                          <p className="font-bold text-xs">

                            {
                              question
                                ?.questionNumber
                            }
                            .{" "}
                            {
                              question
                                ?.questionText
                            }

                          </p>

                          {question
                            ?.isRequired && (

                            <p className="text-[9px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                              Required
                            </p>

                          )}

                          <p
                            className={`text-[9px] rounded-full mx-1 px-2 py-1 ${
                              question
                                ?.isActive
                                ? "bg-lime-100 text-lime-700"
                                : "bg-gray-200 text-gray-500"
                            }`}
                          >

                            {
                              question
                                ?.isActive
                                ? "Active"
                                : "Inactive"
                            }

                          </p>

                        </div>

                        {question
                          ?.questionDescription && (

                          <p className="text-[10px] text-gray-600 mt-1">
                            {
                              question
                                .questionDescription
                            }
                          </p>

                        )}

                        <div className="flex justify-start items-center flex-wrap mt-1">

                          <p className="text-[9px] text-gray-500 mr-3">

                            Type: {
                              question
                                ?.questionType
                            }

                          </p>

                          <p className="text-[9px] text-gray-500 mr-3">

                            Purpose: {
                              question
                                ?.questionPurpose
                            }

                          </p>

                          <p className="text-[9px] text-gray-500">

                            Sequence: {
                              question
                                ?.displaySequence
                            }

                          </p>

                        </div>

                      </div>

                      <div className="flex justify-start items-center">

                        <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-gray-100">

                          <PencilSquareIcon
                            onClick={
                              () =>
                                handleEditQuestion(
                                  question
                                )
                            }
                            className="h-4 w-4 text-orange-600 cursor-pointer"
                          />

                        </div>

                        <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-gray-100">

                          <TrashIcon
                            onClick={
                              () =>
                                handleOpenDeleteModal(
                                  question._id
                                )
                            }
                            className="h-4 w-4 text-orange-600 cursor-pointer"
                          />

                        </div>

                      </div>

                    </div>

                    {/* Options */}

                    {question
                      ?.options
                      ?.length >
                      0 && (

                      <div className="mt-2 ml-2">

                        {[
                            ...(
                                question.options ||
                                []
                            ),
                            ]
                            .sort(
                        // question.options
                        //   ?.sort(
                            (
                              a,
                              b
                            ) =>
                              (
                                a
                                  ?.displaySequence ||
                                0
                              ) -
                              (
                                b
                                  ?.displaySequence ||
                                0
                              )
                          )
                          ?.map(
                            (
                              option,
                              optionIndex
                            ) => (

                              <span
                                key={
                                  optionIndex
                                }
                                className="inline-block text-[9px] bg-gray-100 rounded px-2 py-1 mr-1 mt-1"
                              >

                                {
                                  option
                                    ?.optionLabel
                                }

                              </span>

                            )
                          )}

                      </div>

                    )}

                  </div>

                )
              )}

          </div>

          {/* Add / Edit Question */}

          <div className="bg-gray-200 rounded m-1 p-1">

            <div className="flex justify-between items-center m-1 p-1">

              <p className="font-bold text-xs">

                {
                  editQuestionId
                    ? "Update Question"
                    : "Add Question"
                }

              </p>

              {editQuestionId && (

                <XMarkIcon
                  onClick={
                    handleCancelEdit
                  }
                  className="h-5 w-5 text-orange-600 cursor-pointer"
                />

              )}

            </div>

            <form
              onSubmit={
                handleSubmit
              }
              className="m-1 p-1 text-xs font-poppins"
            >

              {/* Question Number / Sequence */}

              <div className="grid grid-cols-12 gap-1">

                <div className="col-span-3">

                  <input
                    type="number"
                    name="questionNumber"
                    placeholder="Question #"
                    value={
                      formData.questionNumber
                    }
                    onChange={
                      handleChange
                    }
                    className="w-full rounded p-2 m-1"
                  />

                </div>

                <div className="col-span-3">

                  <input
                    type="number"
                    name="displaySequence"
                    placeholder="Display Sequence"
                    value={
                      formData.displaySequence
                    }
                    onChange={
                      handleChange
                    }
                    className="w-full rounded p-2 m-1"
                  />

                </div>

              </div>

              {/* Question Text */}

              <textarea
                name="questionText"
                placeholder="Enter question"
                value={
                  formData.questionText
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
                rows={3}
              />

              {/* Description */}

              <textarea
                name="questionDescription"
                placeholder="Optional question description"
                value={
                  formData.questionDescription
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
                rows={2}
              />

              {/* Response Guidance */}

              <textarea
                name="responseGuidance"
                placeholder="Optional response guidance"
                value={
                  formData.responseGuidance
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
                rows={2}
              />

              {/* Type / Purpose */}

              <div className="grid grid-cols-12 gap-1">

                <div className="col-span-6">

                  <select
                    name="questionType"
                    value={
                      formData.questionType
                    }
                    onChange={
                      handleQuestionTypeChange
                    }
                    className="w-full rounded p-2 m-1"
                  >

                    <option value="reflection">
                      Reflection
                    </option>

                    <option value="short_text">
                      Short Text
                    </option>

                    <option value="long_text">
                      Long Text
                    </option>

                    <option value="single_select">
                      Single Select
                    </option>

                    <option value="multi_select">
                      Multi Select
                    </option>

                    <option value="checkbox_confirmation">
                      Checkbox Confirmation
                    </option>

                  </select>

                </div>

                <div className="col-span-6">

                  <select
                    name="questionPurpose"
                    value={
                      formData.questionPurpose
                    }
                    onChange={
                      handleChange
                    }
                    className="w-full rounded p-2 m-1"
                  >

                    <option value="reflection">
                      Reflection
                    </option>

                    <option value="participation_intent">
                      Participation Intent
                    </option>

                    <option value="in_person_confirmation">
                      In Person Confirmation
                    </option>

                    <option value="other">
                      Other
                    </option>

                  </select>

                </div>

              </div>

              {/* Response Length */}

              {(
                formData.questionType ===
                  "reflection" ||
                formData.questionType ===
                  "short_text" ||
                formData.questionType ===
                  "long_text"
              ) && (

                <div className="grid grid-cols-12 gap-1">

                  <div className="col-span-6">

                    <input
                      type="number"
                      name="minimumResponseLength"
                      placeholder="Minimum response length"
                      value={
                        formData.minimumResponseLength
                      }
                      onChange={
                        handleChange
                      }
                      className="w-full rounded p-2 m-1"
                    />

                  </div>

                  <div className="col-span-6">

                    <input
                      type="number"
                      name="maximumResponseLength"
                      placeholder="Maximum response length"
                      value={
                        formData.maximumResponseLength
                      }
                      onChange={
                        handleChange
                      }
                      className="w-full rounded p-2 m-1"
                    />

                  </div>

                </div>

              )}

              {/* Options */}

              {(
                formData.questionType ===
                  "single_select" ||
                formData.questionType ===
                  "multi_select"
              ) && (

                <div className="bg-white rounded m-1 p-2">

                  <div className="flex justify-between items-center">

                    <p className="font-bold text-xs">
                      Options
                    </p>

                    <PlusIcon
                      onClick={
                        handleAddOption
                      }
                      className="h-5 w-5 text-orange-600 cursor-pointer"
                    />

                  </div>

                  {formData.options
                    ?.map(
                      (
                        option,
                        index
                      ) => (

                        <div
                          key={
                            index
                          }
                          className="grid grid-cols-12 gap-1 items-center"
                        >

                          <input
                            type="text"
                            value={
                              option.optionLabel
                            }
                            onChange={
                              e =>
                                handleOptionChange(
                                  index,
                                  "optionLabel",
                                  e.target.value
                                )
                            }
                            placeholder="Option label"
                            className="col-span-5 rounded p-2 m-1"
                          />

                          <input
                            type="text"
                            value={
                              option.optionValue
                            }
                            onChange={
                              e =>
                                handleOptionChange(
                                  index,
                                  "optionValue",
                                  e.target.value
                                )
                            }
                            placeholder="Option value"
                            className="col-span-5 rounded p-2 m-1"
                          />

                          <div className="col-span-2 flex justify-center">

                            <TrashIcon
                              onClick={
                                () =>
                                  handleRemoveOption(
                                    index
                                  )
                              }
                              className="h-4 w-4 text-orange-600 cursor-pointer"
                            />

                          </div>

                        </div>

                      )
                    )}

                </div>

              )}

              {/* Controls */}

              <div className="bg-white rounded m-1 p-2">

                <div className="flex justify-start items-center">

                  <input
                    type="checkbox"
                    name="isRequired"
                    checked={
                      formData.isRequired
                    }
                    onChange={
                      handleChange
                    }
                    className="m-1"
                  />

                  <p className="text-xs font-bold text-gray-700 m-1">
                    Required Question
                  </p>

                </div>

                <div className="flex justify-start items-center">

                  <input
                    type="checkbox"
                    name="isActive"
                    checked={
                      formData.isActive
                    }
                    onChange={
                      handleChange
                    }
                    className="m-1"
                  />

                  <p className="text-xs font-bold text-gray-700 m-1">
                    Active Question
                  </p>

                </div>

              </div>

              {/* Actions */}

              <div
                id="actions"
                className="bg-white m-1 px-1 py-3 rounded"
              >

                <EtBtnFormBasic
                  name={
                    editQuestionId
                      ? "Update Question"
                      : "Add Question"
                  }
                  type={"create"}
                  handleClick={
                    handleSubmit
                  }
                  disabled={
                    isCreating ||
                    isUpdating ||
                    !formData.questionText
                      ?.trim()
                  }
                />

                {editQuestionId && (

                  <EtBtnFormBasic
                    name={"Cancel Edit"}
                    type={"cancel"}
                    handleClick={
                      handleCancelEdit
                    }
                  />

                )}

              </div>

            </form>

          </div>

        </div>

      )}

      {/* Delete Confirmation */}

      <Modal
        isOpen={
          isDeleteModalOpen
        }
        onClose={
          handleCloseDeleteModal
        }
        onConfirm={
          handleDeleteQuestion
        }
      />

    </div>

  )

}

export default EtOrgIntentionQuestionManager